// A trial store's no-login session (ORD-514). The capture exe opens the link
// /t/<store-id>/<token>; the app keeps that pair and sends it as the bearer
// token `trial:<store-id>:<token>`, which the API checks on every request. The
// link is the whole credential: nothing is exchanged for it.
//
// Kept in localStorage so the prospect stays in across tabs and restarts for
// the trial's 24h. Logging out clears it, which is also the way back to Auth0
// for anyone who opened a link in a browser that has a real session.

import { readJSON, removeKey, safeStorage, writeJSON } from '@shared/helpers/safe-storage';
import { z } from 'zod';

const trialSessionSchema = z.object({ storeId: z.string().min(1), token: z.string().min(1) });

export type TrialSession = z.infer<typeof trialSessionSchema>;

const STORAGE_KEY = 'orderbot:trial-session';

const LINK_PATH = /^\/t\/([^/]+)\/([^/]+)\/?$/;

/** The session a trial link carries, or null when pathname isn't one. */
export function parseTrialLink(pathname: string): TrialSession | null {
  const match = LINK_PATH.exec(pathname);
  if (!match) {
    return null;
  }

  return { storeId: decodeURIComponent(match[1]), token: decodeURIComponent(match[2]) };
}

/** The API bearer token for a session. Mirrors models.TrialBearerToken. */
export function trialBearerToken(session: TrialSession): string {
  return `trial:${session.storeId}:${session.token}`;
}

let active: TrialSession | null = null;

/**
 * Decides at boot whether this page load is a trial session: a trial link
 * starts (or replaces) one, otherwise a saved one resumes. Call once, before
 * the auth provider is chosen.
 */
export function initTrialSession(pathname: string = window.location.pathname): TrialSession | null {
  const fromLink = parseTrialLink(pathname);
  if (fromLink) {
    save(fromLink);
    active = fromLink;
  } else {
    active = load();
  }

  return active;
}

/** The session initTrialSession settled on, if any. */
export function getTrialSession(): TrialSession | null {
  return active;
}

export function isTrialMode(): boolean {
  return active !== null;
}

/**
 * Why a Companion-driven action (receiving, a remote upload) is disabled in a
 * trial: the trial's data came from the capture exe, and no Companion runs on
 * the store's PC until it becomes a real store. ORD-536 brings receiving to
 * trials as a dry run.
 */
export const TRIAL_NO_COMPANION_REASON =
  "Not available in the demo: this runs through the Orderbot Companion on your store's computer.";

/** Ends the session: the next page load is an ordinary Auth0 one. */
export function clearTrialSession(): void {
  active = null;
  removeKey(safeStorage('local'), STORAGE_KEY);
}

// Storage can be unavailable (private windows, blocked site data). The session
// then lasts as long as the page, and the link from the exe re-enters it.
function save(session: TrialSession): void {
  writeJSON(safeStorage('local'), STORAGE_KEY, session);
}

// Unreadable or corrupt — start without a session.
function load(): TrialSession | null {
  const parsed = trialSessionSchema.safeParse(readJSON(safeStorage('local'), STORAGE_KEY));

  return parsed.success ? parsed.data : null;
}
